/**
 * Departments Selectors
 * Selectors for accessing department state
 */

import { createSelector } from '@reduxjs/toolkit';
import { DepartmentsState } from '../../types/department';

interface RootState {
  departments: DepartmentsState;
}

// Base selector
const selectDepartmentsState = (state: RootState) => state.departments;

// Select all departments
export const selectAllDepartments = createSelector(
  [selectDepartmentsState],
  (departmentsState) => departmentsState.departments,
);

// Select active departments only
export const selectActiveDepartments = createSelector(
  [selectAllDepartments],
  (departments) => departments.filter((d) => d.status === 'active'),
);

// Select departments loading state
export const selectDepartmentsLoading = createSelector(
  [selectDepartmentsState],
  (departmentsState) => departmentsState.isLoading,
);

// Select departments error
export const selectDepartmentsError = createSelector(
  [selectDepartmentsState],
  (departmentsState) => departmentsState.error,
);

// Select selected outlet ID
export const selectSelectedOutletId = createSelector(
  [selectDepartmentsState],
  (departmentsState) => departmentsState.selectedOutletId,
);

// Select departments count
export const selectDepartmentsCount = createSelector(
  [selectAllDepartments],
  (departments) => departments.length,
);

// Select department by ID
export const selectDepartmentById = (departmentId: string) =>
  createSelector([selectAllDepartments], (departments) =>
    departments.find((d) => d.id === departmentId),
  );

// Select departments sorted by display order
export const selectDepartmentsSorted = createSelector(
  [selectAllDepartments],
  (departments) =>
    [...departments].sort((a, b) => a.display_order - b.display_order),
);

// Select departments grouped by status
export const selectDepartmentsByStatus = createSelector(
  [selectAllDepartments],
  (departments) => ({
    active: departments.filter((d) => d.status === 'active'),
    inactive: departments.filter((d) => d.status === 'inactive'),
  }),
);
